import { partial } from 'lodash';
import PropTypes from 'prop-types';
import cx from 'classnames/dedupe';
import hexToRgba from 'hex-to-rgba';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import useReactRouter from 'use-react-router';
import { useHotkeys } from 'react-hotkeys-hook';
import useEventListener from 'use-event-listener';
import React, { useState, useRef, useEffect, useMemo } from 'react';

import colours from '~/utils/colours';
import Modal from '~/App/Components/Modal';
import { createRestApi } from '~/App/Api/rest';
import useDebounce from '~/react-hooks/use-debounce';
import { getProfileName } from '~/BonjorosList/util';
import ProfileImage from '~/App/Components/ProfileImage';
import { SpinnerNew } from '~/App/Components/Shared/Spinner';

const InputContainer = styled.div`
  display: flex;
  padding: 15px 0;
  align-items: center;
  justify-content: center;
  border-bottom: 2px solid ${colours.borderLightGrey};
`;

const Input = styled.input`
  width: 550px;
  border: none;
  outline: none;
  font-size: 2em;
  font-weight: bold;
`;

const SearchContainer = styled.div``;

const SearchResult = styled.div`
  display: flex;
  padding: 5px 0 5px 20px;
  align-items: center;
  transition-property: background-color;
  transition: 0.2s ease-in-out;

  &:hover {
    background-color: ${hexToRgba(colours.blue, 0.2)};
  }

  &:hover,
  &:focus {
    text-decoration: none;
  }
`;
SearchResult.defaultProps = { role: 'button' };

const HotkeyText = styled.span`
  font-size: 1.25em;
  font-weight: bold;
`;

const HelpBlock = styled.div`
  padding: 5px 15px;
  color: ${colours.greyMediumDark};
  background-color: ${colours.greyMedium};
`;

const ProfileName = styled.span`
  color: ${colours.greyDark};
  font-weight: bold;
  font-size: 1.25em;
  margin-left: 10px;
`;

const ProfileCompany = styled.span`
  color: ${colours.grey};
  font-weight: bold;
  font-size: 1em;
  margin-left: 10px;
`;

const GreetReason = styled(ProfileCompany)``;

export function openGoto() {
  window.dispatchEvent(new Event('goto::open'));
}

export function closeGoto() {
  window.dispatchEvent(new Event('goto::close'));
}

function TaskSearchResult({ searchResult, onClick }) {
  const greet = searchResult;

  return (
    <React.Fragment>
      {greet.profiles.map((profile) => (
        <Link key={greet.id + profile.id} to={`/home/greet/${greet.id}`}>
          <SearchResult onClick={onClick}>
            <ProfileImage profile={profile} size={25} />
            <ProfileName>{getProfileName(profile)}</ProfileName>
            {profile.work.current && (
              <ProfileCompany>{profile.work.current}</ProfileCompany>
            )}
            <GreetReason>{greet.event.message}</GreetReason>
          </SearchResult>
        </Link>
      ))}
    </React.Fragment>
  );
}

TaskSearchResult.propTypes = {
  searchResult: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
};

function ResultSearchResult({ searchResult, onClick }) {
  const { profile, greet } = searchResult;

  return (
    <Link to={`/results/${greet.id}/${profile.id}`}>
      <SearchResult onClick={partial(onClick, greet.id)}>
        <ProfileImage profile={profile} size={25} />
        <ProfileName>{getProfileName(profile)}</ProfileName>
        {profile.work.current && (
          <ProfileCompany>{profile.work.current}</ProfileCompany>
        )}
        <GreetReason>{greet.event.message}</GreetReason>
      </SearchResult>
    </Link>
  );
}

ResultSearchResult.propTypes = {
  searchResult: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
};

export default function Goto() {
  const [isOpen, setOpen] = useState(false);
  const [input, setInput] = useState();
  const search = useDebounce(input, 1000);
  const [results, setResults] = useState();
  const [isLoading, setLoading] = useState(false);
  const inputRef = useRef();
  const { match } = useReactRouter();

  const SearchResultComponent = useMemo(() => {
    switch (match.path) {
      case '/home':
        return TaskSearchResult;
      case '/results':
        return ResultSearchResult;
      case '/replies':
        return ResultSearchResult;
    }
  }, [match.path]);

  function handleOpenHotkeyPressed(evt) {
    evt.preventDefault();

    setOpen(true);
  }

  const isMac = window.navigator.platform.startsWith('Mac');

  const openHotkey = isMac ? 'cmd+k' : 'ctrl+k';

  useHotkeys(openHotkey, handleOpenHotkeyPressed);

  useHotkeys('esc', partial(setOpen, false));

  useEventListener('goto::open', partial(setOpen, true));

  useEventListener('goto::close', partial(setOpen, false));

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current.focus(), 100);
    } else {
      setTimeout(() => {
        setResults(null);
        setInput(null);
      }, 1000);
    }
  }, [isOpen]);

  useEffect(() => {
    async function searchAsync(search) {
      setLoading(true);

      let api = null;

      switch (match.path) {
        case '/results':
          api = createRestApi('/api/v2/greets/results/');
          break;
        case '/home':
          api = createRestApi('/api/v2/greets/todo/');
          break;
        case '/replies':
          api = createRestApi('/api/v2/greets/replies/');
          break;
        default:
          throw new Error('Invalid route matched');
      }

      const results = await api.getAll({ search });

      setResults(results);

      setLoading(false);
    }

    if (typeof search === 'string' && search.length >= 3) {
      searchAsync(search);
    }
  }, [search]);

  if (!isOpen) {
    return null;
  }

  const modalProps = {
    isOpen,
    hideActions: true,
    onRequestClose: partial(setOpen, false),
    styles: {
      panel: ({ base }) => ({
        ...base,
        padding: 0,
        overflow: 'hidden',
      }),
    },
  };

  function handleResultClick() {
    setOpen(false);
  }

  return (
    <Modal {...modalProps}>
      <InputContainer>
        <Input
          placeholder='Go to...'
          ref={inputRef}
          onChange={(evt) => setInput(evt.target.value)}
        />
        {isLoading && <SpinnerNew />}
      </InputContainer>
      {results && results.data.length > 0 && (
        <SearchContainer>
          {results.data.map((searchResult) => (
            <SearchResultComponent
              key={searchResult.id}
              searchResult={searchResult}
              onClick={handleResultClick}
            />
          ))}
        </SearchContainer>
      )}
      <HelpBlock>
        <HotkeyText>{isMac ? '⌘' : 'ctrl'}+k</HotkeyText> to show &nbsp;
        <HotkeyText>esc</HotkeyText> to dismiss
      </HelpBlock>
    </Modal>
  );
}
